/**
 * useSubmitAnswer hook
 * Manages answer submission with React Query mutations
 * Implements optimistic updates and automatic message refetch
 * Supports both MCP (polling) and serverless (synchronous) modes
 *
 * US-001 (content-moderation sprint): Added CONTENT_BLOCKED error-code mapping
 * to both the MCP (mcpMutation) and serverless (serverlessMutation) onError handlers
 * so a 422 CONTENT_BLOCKED from Hook A or Hook B1 surfaces a child-friendly rephrase
 * prompt rather than a generic error message (AC-2).
 * Enforces TO-LL-038 (CORS/event) on the backend side; this file only consumes the
 * error code/message returned by error_response().
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { createDecision } from '@/services/decisions';
import { submitServerlessAnswer } from '@/services/sessions';
import { getSnapshot as getAnnotationSnapshot } from '@/lib/passageAnnotationStore';
import { countByShape } from '@/lib/passageAnnotations';
import type { CreateDecisionRequest, DecisionError } from '@/types/decision';
import type { Message, SessionMessagesResponse, TutorTurnResponse } from '@/types/session';

/**
 * Return type for useSubmitAnswer hook
 */
export interface UseSubmitAnswerReturn {
  /**
   * Submit an answer.
   *
   * BUG-20260721: resolves `true` when the turn was accepted and `false` when it
   * failed, so the caller can decide whether to restore the student's typed text.
   * It NEVER rejects — existing fire-and-forget callers do not await it, and a
   * rejected promise nobody awaits becomes an unhandled rejection. Error detail
   * is still exposed through `error`, unchanged.
   */
  submitAnswer: (content: string) => Promise<boolean>;

  /** True while submission is in progress */
  isSubmitting: boolean;

  /** Error object if submission failed */
  error: DecisionError | null;

  /** True after successful submission */
  isSuccess: boolean;

  /** Reset mutation state */
  reset: () => void;

  /**
   * Serverless response data (only present for serverless sessions)
   * Contains tutor message, progress, pebbles awarded, and session summary
   */
  serverlessResponse?: TutorTurnResponse | null;
}

/**
 * Hook to submit student answers with optimistic updates
 * Immediately shows user's message in chat before API confirmation
 * Automatically triggers message refetch on success
 * Supports both MCP (createDecision + poll) and serverless (submitServerlessAnswer + direct cache update) modes
 *
 * @param sessionId - The session ID to submit answers to
 * @param mode - The session mode (mcp | serverless | undefined). Undefined treated as MCP.
 * @returns Object with submitAnswer function and state flags
 *
 * @example
 * ```typescript
 * const { submitAnswer, isSubmitting, error } = useSubmitAnswer(sessionId, mode);
 *
 * const handleSubmit = (content: string) => {
 *   submitAnswer(content);
 * };
 * ```
 */
export function useSubmitAnswer(
  sessionId: string,
  mode?: 'mcp' | 'serverless'
): UseSubmitAnswerReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // MCP mutation: uses createDecision and invalidates query for polling
  const mcpMutation = useMutation<
    { decisionId: string; sessionId: string; timestamp: string },
    DecisionError,
    string,
    { previousMessages?: SessionMessagesResponse }
  >({
    mutationFn: async (content: string) => {
      // Client-side validation
      if (!content.trim()) {
        throw {
          code: 'VALIDATION_ERROR',
          message: 'Answer cannot be empty',
        } as DecisionError;
      }

      if (!user) {
        throw {
          code: 'UNAUTHORIZED',
          message: 'You must be logged in to submit an answer',
        } as DecisionError;
      }

      // Construct request payload
      const request: CreateDecisionRequest = {
        userId: user.userId,
        sessionId,
        role: 'student',
        decisionType: 'answer',
        content: content.trim(),
      };

      // Call API to create decision
      return await createDecision(request);
    },

    // onMutate: Add optimistic message to cache before API call
    onMutate: async (content: string) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: ['session', sessionId, 'messages'],
      });

      // Get current cached data
      const previousMessages = queryClient.getQueryData<SessionMessagesResponse>([
        'session',
        sessionId,
        'messages',
      ]);

      // Optimistically update cache with user's message
      if (previousMessages && user) {
        // BUG-20260220-185500: Derive questionNumber from cached messages so optimistic
        // message appears in the correct question group, not Introduction - 2026-02-20
        const currentQNum =
          previousMessages.messages
            .filter((m) => m.questionNumber != null)
            .reduce((max, m) => Math.max(max, m.questionNumber!), 0) || 1;

        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`, // Temporary ID until server responds
          role: 'student',
          content: content.trim(),
          timestamp: new Date().toISOString(),
          decisionType: 'answer',
          questionNumber: currentQNum,
        };

        queryClient.setQueryData<SessionMessagesResponse>(['session', sessionId, 'messages'], {
          ...previousMessages,
          messages: [...previousMessages.messages, optimisticMessage],
        });
      }

      // Return context with previous data for rollback if needed
      return { previousMessages };
    },

    // onSuccess: Invalidate query to trigger refetch and get tutor response
    onSuccess: () => {
      // Invalidate messages query to trigger immediate refetch
      // This ensures we get the tutor's feedback as quickly as possible
      queryClient.invalidateQueries({
        queryKey: ['session', sessionId, 'messages'],
      });
    },

    // onError: Rollback optimistic update if submission fails
    onError: (error, _content, context) => {
      // Restore previous cache state
      if (context?.previousMessages) {
        queryClient.setQueryData(['session', sessionId, 'messages'], context.previousMessages);
      }
      // Map demo limit error codes to user-friendly messages
      if (error.code === 'DEMO_ATTEMPT_LIMIT') {
        error.message = 'Attempt limit reached for this question.';
      } else if (error.code === 'DEMO_QUESTION_LIMIT') {
        error.message = 'Question limit reached for this session.';
      } else if (error.code === 'CONTENT_BLOCKED') {
        // US-001 (content-moderation sprint): Map Hook A 422 CONTENT_BLOCKED to the
        // child-friendly rephrase prompt. The backend already returns this string as
        // error.message; this branch ensures it is surfaced even if the backend
        // message is absent (safe degradation per CCM-002 parallel_paths requirement).
        // Implements US-001 T3 (AC-2); enforces TO-LL-015 (no framework-coupling here).
        error.message = 'Let’s keep it about the story — please try again.';
      }
    },
  });

  // Serverless mutation: uses submitServerlessAnswer and directly updates cache
  const serverlessMutation = useMutation<
    TutorTurnResponse,
    DecisionError,
    string,
    { previousMessages?: SessionMessagesResponse }
  >({
    mutationFn: async (content: string) => {
      // Client-side validation
      if (!content.trim()) {
        throw {
          code: 'VALIDATION_ERROR',
          message: 'Please type your answer before submitting.',
        } as DecisionError;
      }

      if (!user) {
        throw {
          code: 'UNAUTHORIZED',
          message: 'You must be logged in to submit an answer',
        } as DecisionError;
      }

      // BUG-20260721: retry ONCE on PROVIDER_TIMEOUT, as a brand-new request.
      //
      // The backend now bounds its LLM work to a turn budget below API Gateway's
      // hard 29 s integration timeout, so a slow tutor returns a real
      // PROVIDER_TIMEOUT instead of losing the race to a bare gateway 504. That
      // response is emitted upstream of the conversation append, the attempt
      // increment and the DynamoDB write, so a timed-out turn persists NOTHING.
      // Resending is therefore safe: it cannot double-append the answer, double
      // count an attempt, or double-award pebbles.
      //
      // The retry is deliberately a fresh HTTP request rather than an in-Lambda
      // retry, so it gets its own full 29 s window. Splitting one window across
      // two attempts would force each to ~13 s, which would abandon the slow but
      // genuinely successful turns (19 s, 23 s) seen during provider degradation.
      //
      // Only PROVIDER_TIMEOUT is retried. Rate limits, validation failures,
      // moderation blocks and completed sessions are all deterministic: resending
      // them would just burn another call to reach the same answer.
      // Passage annotation (Phase 1): read the student's current tally straight from the
      // annotation store rather than threading it down through the chat page. The store is
      // already a module-level singleton keyed by sessionId — that is why it exists, since both
      // passage mounts share it — so this is the same value the passage is displaying, with no
      // prop drilling and nothing for a caller to forget to pass.
      const annotationCounts = countByShape(getAnnotationSnapshot(sessionId).marks);

      try {
        return await submitServerlessAnswer(sessionId, content.trim(), annotationCounts);
      } catch (err) {
        if ((err as DecisionError | undefined)?.code !== 'PROVIDER_TIMEOUT') {
          throw err;
        }
        return await submitServerlessAnswer(sessionId, content.trim(), annotationCounts);
      }
    },

    // onMutate: Add optimistic student message to cache
    onMutate: async (content: string) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['session', sessionId, 'messages'],
      });

      // Get current cached data
      const previousMessages = queryClient.getQueryData<SessionMessagesResponse>([
        'session',
        sessionId,
        'messages',
      ]);

      // Optimistically update cache with student's message
      if (previousMessages && user) {
        // BUG-20260220-185500: Derive questionNumber from cached messages so optimistic
        // message appears in the correct question group, not Introduction - 2026-02-20
        const currentQNum =
          previousMessages.messages
            .filter((m) => m.questionNumber != null)
            .reduce((max, m) => Math.max(max, m.questionNumber!), 0) || 1;

        const optimisticMessage: Message = {
          id: `temp-${Date.now()}`,
          role: 'student',
          content: content.trim(),
          timestamp: new Date().toISOString(),
          decisionType: 'answer',
          questionNumber: currentQNum,
        };

        queryClient.setQueryData<SessionMessagesResponse>(['session', sessionId, 'messages'], {
          ...previousMessages,
          messages: [...previousMessages.messages, optimisticMessage],
        });
      }

      return { previousMessages };
    },

    // onSuccess: Directly insert tutor response into cache (no polling needed)
    onSuccess: (data, _content, _context) => {
      // Get current cache (includes optimistic student message)
      const currentMessages = queryClient.getQueryData<SessionMessagesResponse>([
        'session',
        sessionId,
        'messages',
      ]);

      if (currentMessages) {
        // BUG-20260616-221535: Guard against null/undefined id before .startsWith to prevent
        // TypeError from flipping the mutation to 'error' and discarding serverlessResponse,
        // which suppressed CelebrationPopup. A null-id message is treated as non-temp (retained). - 2026-06-17
        if (import.meta.env.DEV && currentMessages.messages.some((m) => m.id == null))
          console.warn(
            '[useSubmitAnswer] cache message with missing id detected',
            currentMessages.messages
          );
        // Remove temp optimistic message and add both real student + tutor messages
        const messagesWithoutTemp = currentMessages.messages.filter(
          (m) => !m.id?.startsWith('temp-')
        );

        // BUG-20260220-172340: Derive questionNumber for this answer from the response.
        // When pebblesAwarded is present, the question just completed and currentQuestion
        // has already been incremented to the NEXT question. Subtract 1 to get the question
        // number that was actually answered. Without pebblesAwarded the question is still
        // in progress, so currentQuestion is the correct (current) question number. - 2026-02-20
        // BUG-20260227-122919: Also check maxAttemptsReached — when the question is force-completed
        // due to max attempts, pebblesAwarded is absent but currentQuestion has still been
        // incremented. Use the same subtract-1 logic to get the correct question number - 2026-02-27.
        const questionNumber =
          data.pebblesAwarded || data.maxAttemptsReached
            ? data.questionProgress.currentQuestion - 1
            : data.questionProgress.currentQuestion;

        // BUG-20260213-132402: Create student message FIRST to ensure earlier timestamp - 2026-02-13
        // BUG-20260220-172340: Add decisionType and questionNumber so groupMessagesByQuestionCycle()
        // places this message in the correct question group instead of 'Introduction' - 2026-02-20
        const studentMessage: Message = {
          id: `student-${Date.now()}`,
          role: 'student',
          content: _content.trim(),
          timestamp: new Date().toISOString(),
          decisionType: 'answer',
          questionNumber,
        };

        // BUG-20260220-172340: Add decisionType and questionNumber to tutor feedback message
        // so it is grouped with the student answer in the correct question cycle - 2026-02-20
        const tutorMessage: Message = {
          id: `serverless-${Date.now()}`,
          role: 'tutor',
          content: data.tutorMessage,
          timestamp: new Date().toISOString(),
          decisionType: 'feedback',
          questionNumber,
        };

        // Update cache with both messages and updated session status
        queryClient.setQueryData<SessionMessagesResponse>(['session', sessionId, 'messages'], {
          ...currentMessages,
          status: data.sessionStatus,
          messages: [...messagesWithoutTemp, studentMessage, tutorMessage],
        });
      }

      // BUG-20260223-162648: Only invalidate when question completed (pebblesAwarded present).
      // The invalidation picks up backend-injected Q2 text. For regular wrong-answer hints,
      // skip invalidation to avoid a race condition where DynamoDB eventually-consistent reads
      // return stale data, causing the student's answer to disappear from the cache.
      // Delay 500ms to allow DynamoDB write consistency to resolve. - 2026-02-23
      // BUG-20260227-122919: Also invalidate when maxAttemptsReached — the force-complete path
      // injects Q2 text into conversation[] and we need the poll to pick it up - 2026-02-27.
      if (data.pebblesAwarded || data.maxAttemptsReached) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['session', sessionId, 'messages'],
          });
        }, 500);
      }

      // BUG-20260222-104604: Invalidate user profile and sessions caches when pebbles are
      // awarded or the session auto-completes. Without this, the header PebblesDisplay reads
      // stale cached profile data (30s staleTime) while the dashboard shows the correct total.
      // Copied from the reference implementation in useCompleteSession.ts lines 44-52. - 2026-02-22
      if (data.pebblesAwarded || data.sessionStatus === 'completed') {
        queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
        queryClient.invalidateQueries({ queryKey: ['sessions'] });
      }
    },

    // onError: Rollback optimistic update and map error codes to user-friendly messages
    onError: (error, _content, context) => {
      // Restore previous cache state
      if (context?.previousMessages) {
        queryClient.setQueryData(['session', sessionId, 'messages'], context.previousMessages);
      }

      // Map serverless-specific error codes
      if (error.code === 'RATE_LIMITED') {
        error.message = 'The tutor is busy right now. Please wait a moment and try again.';
      } else if (error.code === 'PROVIDER_TIMEOUT') {
        // BUG-20260721: reached only after the automatic retry above has ALSO timed
        // out. Both attempts failed upstream of any state-mutating write (the
        // server-side turn claim is released on this path — see
        // BUG-20260731-163953 DD-12), so the student genuinely has not been
        // charged an attempt. Say so plainly: without the reassurance a
        // child reasonably assumes a failed submission cost them one of their tries.
        error.message =
          'Oops! The tutor is thinking very slowly right now. Your answer was not ' +
          'counted and you have not lost a try, so please send it again.';
      } else if (error.code === 'TURN_ALREADY_APPLIED' || error.code === 'COOLDOWN_ACTIVE') {
        // BUG-20260731-163953 (RC-001, T-6, 2026-07-31): the server-side
        // optimistic-concurrency claim makes both codes reachable for a
        // genuine concurrent sibling request (e.g. the PROVIDER_TIMEOUT
        // retry above racing a first request that actually committed, or a
        // double-tap). Both are BENIGN and self-healing: the rollback above
        // already restored the pre-optimistic cache, which for the LOSING
        // request of a double-tap can be stale relative to the WINNING
        // request's real messages — and serverless chat has no polling to
        // heal that on its own (refetchInterval is false for
        // mode==='serverless'). Reuse the existing delayed-invalidate
        // pattern (see the success handler above, BUG-20260223-162648) so
        // the cache converges on server truth instead of staying rolled back.
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['session', sessionId, 'messages'],
          });
        }, 500);
        error.message =
          error.code === 'TURN_ALREADY_APPLIED'
            ? 'Your answer is already in. Just a moment while we catch up.'
            : 'Just a moment — catching up on your last answer.';
      } else if (error.code === 'SESSION_COMPLETED') {
        error.message = 'This session has been completed. You cannot submit new answers.';
      } else if (error.code === 'DEMO_ATTEMPT_LIMIT') {
        error.message = 'Attempt limit reached for this question.';
      } else if (error.code === 'DEMO_QUESTION_LIMIT') {
        error.message = 'Question limit reached for this session.';
      } else if (error.code === 'CONTENT_BLOCKED') {
        // US-001 (content-moderation sprint): Map Hook B1 422 CONTENT_BLOCKED to the
        // child-friendly rephrase prompt. The backend already returns this string as
        // error.message; this branch ensures it is surfaced even if the backend
        // message is absent (safe degradation per CCM-002 parallel_paths requirement).
        // Implements US-001 T3 (AC-2); enforces TO-LL-015 (no framework-coupling here).
        error.message = "Let's keep it about the story — please try again.";
      }
    },
  });

  /**
   * Submit an answer to the session
   * Routes to appropriate mutation based on session mode
   * @param content - The answer text to submit
   */
  const submitAnswer = async (content: string): Promise<boolean> => {
    // Validate content before submitting
    if (!content.trim()) {
      return false; // Silently ignore empty submissions
    }

    // BUG-20260721: mutateAsync rather than mutate, so the caller can distinguish
    // success from failure. The rejection is converted to `false` here instead of
    // being re-thrown, for the reason documented on UseSubmitAnswerReturn.
    try {
      // Route to appropriate mutation
      if (mode === 'serverless') {
        await serverlessMutation.mutateAsync(content);
      } else {
        // Default to MCP for undefined or 'mcp' mode
        await mcpMutation.mutateAsync(content);
      }
      return true;
    } catch {
      return false;
    }
  };

  // Combine state from both mutations
  const isSubmitting = mcpMutation.isPending || serverlessMutation.isPending;
  const error = mcpMutation.error || serverlessMutation.error;
  const isSuccess = mcpMutation.isSuccess || serverlessMutation.isSuccess;

  // Reset function that resets both mutations
  const reset = () => {
    mcpMutation.reset();
    serverlessMutation.reset();
  };

  return {
    submitAnswer,
    isSubmitting,
    error,
    isSuccess,
    reset,
    serverlessResponse: serverlessMutation.data,
  };
}
